'use client'

import {useState} from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {VisuallyHidden} from "@radix-ui/react-visually-hidden";
import Image from "next/image";
import {Button} from "@/components/ui/button";
import {Dialog, DialogContent, DialogDescription, DialogTitle} from "@/components/ui/dialog";
import ListingPhoto from "@/components/listing-photo";
import type {UsablePhoto} from "@/lib/listing-photos";

const ZOOM_SIZES = "90vw";

/**
 * Zoom dialog, controlled by its parent: `index` is the position of the photo
 * shown in `photos` (null = closed). Previous/Next (buttons and the Left/Right
 * keys) wrap around and call `onIndexChange`. `onClose` runs when it is
 * dismissed; `onCloseAutoFocus` lets the parent return focus to a thumbnail.
 */
export default function ClassicZoom({photos, index, address, onIndexChange, onClose, onCloseAutoFocus}: {
    photos: UsablePhoto[];
    index: number | null;
    address: string;
    onIndexChange: (index: number) => void;
    onClose: () => void;
    onCloseAutoFocus?: (event: Event) => void,
}) {
    // The src whose large photo has finished loading; the neighbours are preloaded after it.
    const [loadedSrc, setLoadedSrc] = useState<string>();
    const count = photos.length;
    const open = index !== null && index >= 0 && index < count;
    const current = open ? photos[index] : undefined;
    const many = count > 1;
    const prevIndex = open ? (index - 1 + count) % count : 0;
    const nextIndex = open ? (index + 1) % count : 0;
    const label = open ? `Photo ${index + 1} of ${count}` : "";
    const alt = open ? `${address ? `Photo of ${address}` : "Listing photo"} ${index + 1}` : "";

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent className="max-w-[calc(100dvh)] max-h-full p-2 md:p-10"
                           onCloseAutoFocus={onCloseAutoFocus}
                           onKeyDown={(event) => {
                               if (!many || !open) return;
                               if (event.key === "ArrowLeft") {
                                   event.preventDefault();
                                   onIndexChange(prevIndex);
                               } else if (event.key === "ArrowRight") {
                                   event.preventDefault();
                                   onIndexChange(nextIndex);
                               }
                           }}>
                <VisuallyHidden>
                    <DialogTitle>{label}{address ? `, ${address}` : ""}</DialogTitle>
                    <DialogDescription>
                        {many ? "Use the Left and Right arrow keys to see other photos. Escape closes." : "Escape closes."}
                    </DialogDescription>
                </VisuallyHidden>
                {current ? (
                    <div className="relative aspect-[3/2] w-full">
                        <ListingPhoto key={current.src} src={current.src} alt={alt} sizes={ZOOM_SIZES}
                                      fit="contain" priority onLoaded={() => setLoadedSrc(current.src)}/>
                    </div>
                ) : null}
                {open && many ? (
                    <div className="flex items-center justify-center gap-4">
                        <Button type="button" variant="secondary" size="icon" aria-label="Previous photo"
                                onClick={() => onIndexChange(prevIndex)} className="size-11 rounded-full">
                            <ChevronLeft className="size-6" aria-hidden="true"/>
                        </Button>
                        <p aria-live="polite" className="text-center text-sm text-muted-foreground">{label}</p>
                        <Button type="button" variant="secondary" size="icon" aria-label="Next photo"
                                onClick={() => onIndexChange(nextIndex)} className="size-11 rounded-full">
                            <ChevronRight className="size-6" aria-hidden="true"/>
                        </Button>
                    </div>
                ) : open ? (
                    <p aria-live="polite" className="text-center text-sm text-muted-foreground">{label}</p>
                ) : null}
                {open && many && loadedSrc === current?.src ? (
                    // Preload the neighbours (same sizes, so the browser reuses the download).
                    <div hidden aria-hidden="true">
                        {[...new Set([nextIndex, prevIndex])].map((i) => (
                            <div key={photos[i].src} className="relative aspect-[3/2] w-full">
                                <Image src={photos[i].src} alt="" fill sizes={ZOOM_SIZES}
                                       unoptimized loading="eager"/>
                            </div>
                        ))}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
